import { useMemo, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'
import { ArrowRight, FlaskConical, History, X } from 'lucide-react'
import clsx from 'clsx'
import type {
  TestCompletionEndpoint,
  TestCompletionEndpointType,
  TestCompletionMode,
  TestCompletionPreflight,
  TestCompletionRunHistory,
} from '@/api/testCompletion'
import { useLibrary } from '@/hooks/coaching/useLibrary'
import {
  useTestCompletionActions,
  useTestCompletionCapability,
  useTestCompletionHistory,
} from '@/hooks/coaching/useTestCompletion'
import { getApiErrorMessage } from '@/utils/apiError'
import { TestCompletionEndpointSelector } from './TestCompletionEndpointSelector'
import {
  buildEndpointOptions,
  endpointTypeRequiresTarget,
  testCompletionEndpointKey,
} from './endpointOptions'

interface Props {
  learnerId: number
}

const RESUMABLE_STATUSES = new Set(['STOPPED', 'PAUSED', 'FAILED', 'INTERRUPTED'])

export function TestCompletionControl({ learnerId }: Props) {
  const { t } = useTranslation('coaching')
  const capability = useTestCompletionCapability()
  const library = useLibrary(learnerId)
  const actions = useTestCompletionActions(learnerId)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<TestCompletionMode>('WITH_TEACHER_FINALIZATION')
  const [endpointType, setEndpointType] = useState<TestCompletionEndpointType | null>(null)
  const [endpointKey, setEndpointKey] = useState('')
  const [typedConfirmation, setTypedConfirmation] = useState('')
  const history = useTestCompletionHistory(learnerId, open)
  const options = useMemo(
    () => buildEndpointOptions(library.data, mode),
    [library.data, mode],
  )
  const endpoint = useMemo<TestCompletionEndpoint | null>(() => {
    const selected = options.find((option) => testCompletionEndpointKey(option) === endpointKey)
    return selected && !selected.completed
      ? { type: selected.type, contentId: selected.contentId }
      : null
  }, [endpointKey, options])

  if (capability.data?.enabled !== true) return null

  const resetPreview = () => {
    actions.preflight.reset()
    actions.start.reset()
    setTypedConfirmation('')
  }
  const close = () => {
    if (actions.start.isPending) return
    setOpen(false)
    resetPreview()
  }
  const preview = () => {
    if (endpoint == null) return
    actions.preflight.mutate({ completionMode: mode, endpoint })
  }
  const start = () => {
    const plan = actions.preflight.data
    if (plan == null || endpoint == null) return
    actions.start.mutate({
      completionMode: mode,
      endpoint,
      planHash: plan.planHash,
      typedConfirmation: plan.requiresTypedConfirmation ? typedConfirmation : undefined,
    }, { onSuccess: close })
  }
  const changeMode = (nextMode: TestCompletionMode) => {
    setMode(nextMode)
    setEndpointType(null)
    setEndpointKey('')
    resetPreview()
  }
  const changeEndpointType = (nextType: TestCompletionEndpointType) => {
    setEndpointType(nextType)
    const firstOption = options.find((option) => option.type === nextType)
    setEndpointKey(
      !endpointTypeRequiresTarget(nextType) && firstOption
        ? testCompletionEndpointKey(firstOption)
        : '',
    )
    resetPreview()
  }
  const changeEndpoint = (nextEndpointKey: string) => {
    setEndpointKey(nextEndpointKey)
    resetPreview()
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-bold text-cyan-800 transition-colors hover:bg-cyan-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600"
      >
        <FlaskConical className="size-4" aria-hidden="true" />
        {t('testCompletion.button')}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="test-completion-title"
          onClick={close}
        >
          <div
            className="max-h-[90vh] w-full max-w-3xl overscroll-contain overflow-y-auto rounded-3xl border border-cyan-100 bg-slate-50 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sticky top-0 z-10 flex items-start justify-between gap-4 rounded-t-3xl border-b border-cyan-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-700">
                  {t('testCompletion.testCenterBadge')}
                </p>
                <h2 id="test-completion-title" className="mt-1 text-2xl font-black tracking-tight text-slate-950">
                  {t('testCompletion.title')}
                </h2>
                <p className="mt-1 text-sm text-slate-600">{t('testCompletion.description')}</p>
              </div>
              <button
                type="button"
                aria-label={t('testCompletion.close')}
                onClick={close}
                className="rounded-full p-2 text-slate-500 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600"
              >
                <X className="size-5" />
              </button>
            </header>

            <div className="space-y-5 p-6">
              <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5">
                <label className="block max-w-sm space-y-2 text-sm font-bold text-slate-800">
                  <span>{t('testCompletion.mode.label')}</span>
                  <select
                    name="test-completion-mode"
                    value={mode}
                    onChange={(event) => changeMode(event.target.value as TestCompletionMode)}
                    autoComplete="off"
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 font-medium focus-visible:border-cyan-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100"
                  >
                    <option value="WITH_TEACHER_FINALIZATION">{t('testCompletion.mode.withTeacher')}</option>
                    <option value="LEARNER_ACTIVITY_ONLY">{t('testCompletion.mode.learnerOnly')}</option>
                  </select>
                  {mode === 'LEARNER_ACTIVITY_ONLY' ? (
                    <p className="text-xs font-medium leading-5 text-amber-700">
                      {t('testCompletion.mode.learnerOnlyHint')}
                    </p>
                  ) : null}
                </label>
                <TestCompletionEndpointSelector
                  mode={mode}
                  tree={library.data?.tree ?? []}
                  options={options}
                  selectedType={endpointType}
                  endpointKey={endpoint ? endpointKey : ''}
                  loading={library.isLoading}
                  onTypeChange={changeEndpointType}
                  onEndpointChange={changeEndpoint}
                />
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5" aria-live="polite">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-black text-slate-900">{t('testCompletion.preview.title')}</h3>
                  <button
                    type="button"
                    onClick={preview}
                    disabled={endpoint == null || actions.preflight.isPending}
                    className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700 disabled:bg-slate-300"
                  >
                    {actions.preflight.isPending
                      ? t('testCompletion.preview.loading')
                      : t('testCompletion.preview.button')}
                  </button>
                </div>
                {actions.preflight.isError ? (
                  <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700" role="alert">
                    {getApiErrorMessage(actions.preflight.error, t)}
                  </p>
                ) : null}
                {actions.preflight.data ? (
                  <PreflightResult
                    plan={actions.preflight.data}
                    typedConfirmation={typedConfirmation}
                    onTypedConfirmation={setTypedConfirmation}
                  />
                ) : (
                  <p className="mt-4 text-sm text-slate-500">{t('testCompletion.preview.empty')}</p>
                )}
              </section>

              {actions.preflight.data ? (
                <div className={clsx(
                  'rounded-2xl border p-5',
                  actions.preflight.data.blockingError
                    ? 'border-slate-200 bg-slate-100'
                    : 'border-amber-200 bg-amber-50',
                )}>
                  {!actions.preflight.data.blockingError ? (
                    <p className="text-sm font-bold leading-6 text-amber-900">
                      {t('testCompletion.noRollbackWarning')}
                    </p>
                  ) : null}
                  {actions.start.isError ? (
                    <p className="mt-2 text-sm font-semibold text-red-700" role="alert">
                      {t('testCompletion.startError')}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    onClick={start}
                    disabled={
                      actions.start.isPending
                      || actions.preflight.data.blockingError != null
                      || (actions.preflight.data.requiresTypedConfirmation && typedConfirmation !== t('testCompletion.typedValue'))
                    }
                    className="mt-4 w-full rounded-xl bg-cyan-700 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-cyan-800 disabled:bg-slate-300"
                  >
                    {actions.start.isPending ? t('testCompletion.starting') : t('testCompletion.start')}
                  </button>
                </div>
              ) : null}

              <HistorySection learnerId={learnerId} rows={history.data ?? []} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

function PreflightResult({
  plan,
  typedConfirmation,
  onTypedConfirmation,
}: {
  plan: TestCompletionPreflight
  typedConfirmation: string
  onTypedConfirmation: (value: string) => void
}) {
  const { t } = useTranslation(['coaching', 'common'])
  const blockingMessage = plan.blockingError
    ? t(
        `common:apiError.${plan.blockingError.code}`,
        t('testCompletion.preview.blocked', { code: plan.blockingError.code }),
      )
    : null
  const firstItem = plan.items[0]
  const lastItem = plan.items[plan.items.length - 1]
  return (
    <div className="mt-4 space-y-4">
      {firstItem && lastItem ? (
        <div className="rounded-xl border border-cyan-100 bg-cyan-50/70 px-4 py-3">
          <p className="text-[11px] font-black uppercase tracking-[0.16em] text-cyan-700">
            {t('testCompletion.preview.rangeLabel')}
          </p>
          <div className="mt-2 flex items-center gap-2 text-sm font-bold text-slate-900">
            <span className="min-w-0 flex-1 break-words"><ContentMetadataText text={firstItem.title} /></span>
            <ArrowRight className="size-4 shrink-0 text-cyan-700" aria-hidden="true" />
            <span className="min-w-0 flex-1 break-words text-right"><ContentMetadataText text={lastItem.title} /></span>
          </div>
        </div>
      ) : null}
      <div className="grid grid-cols-3 gap-2 text-center">
        {(['total', 'process', 'skip'] as const).map((key) => (
          <div key={key} className="rounded-xl bg-slate-100 px-3 py-3">
            <p className="text-xl font-black text-slate-900">{plan.counts[key]}</p>
            <p className="text-xs font-bold text-slate-500">{t(`testCompletion.counts.${key}`)}</p>
          </div>
        ))}
      </div>
      {plan.blockingError ? (
        <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-700" role="alert">
          {blockingMessage}
        </p>
      ) : null}
      <ol className="divide-y divide-slate-100 rounded-xl border border-slate-200">
        {plan.items.map((item) => {
          const requirements = item.teacherRequirements
          const badges: Array<[string, number]> = requirements ? [
            ['buildup', requirements.buildupGradeCount] as [string, number],
            ['essential', requirements.essentialCheckCount] as [string, number],
            ['workbook', requirements.workbookGradeCount] as [string, number],
            ['audio', requirements.requiredAudioCount] as [string, number],
            ['offline', requirements.offlineInputComponentCount] as [string, number],
          ].filter(([, count]) => Number(count) > 0) : []
          return (
          <li key={`${item.ordinal}:${item.title}`} className="flex items-start gap-3 px-4 py-3 text-sm">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-xs font-black text-white">
              {item.ordinal}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-slate-800"><ContentMetadataText text={item.title} /></span>
              {badges.length > 0 ? (
                <span className="mt-1.5 flex flex-wrap gap-1">
                  {badges.map(([key, count]) => (
                    <span key={String(key)} className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                      {t(`testCompletion.requirements.${key}`, { count })}
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
            <span className={clsx(
              'rounded-full px-2 py-1 text-[11px] font-black',
              item.action === 'PROCESS' ? 'bg-cyan-50 text-cyan-800' : 'bg-slate-100 text-slate-500',
            )}>
              {t(`testCompletion.action.${item.action}`)}
            </span>
          </li>
          )
        })}
      </ol>
      {plan.requiresTypedConfirmation && !plan.blockingError ? (
        <label className="block space-y-2 text-sm font-bold text-slate-800">
          <span>{t('testCompletion.typedPrompt')}</span>
          <input
            name="test-completion-confirmation"
            value={typedConfirmation}
            onChange={(event) => onTypedConfirmation(event.target.value)}
            placeholder={t('testCompletion.typedValue')}
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 focus-visible:border-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100"
          />
        </label>
      ) : null}
    </div>
  )
}

function HistorySection({ learnerId, rows }: { learnerId: number; rows: TestCompletionRunHistory[] }) {
  const { t } = useTranslation(['coaching', 'common'])
  const actions = useTestCompletionActions(learnerId)
  const resume = async (runId: number) => {
    const plan = await actions.resumePreflight.mutateAsync(runId)
    await actions.resume.mutateAsync({ runId, planHash: plan.planHash })
  }
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="flex items-center gap-2 font-black text-slate-900">
        <History className="size-4 text-slate-500" />
        {t('testCompletion.history.title')}
      </h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">{t('testCompletion.history.empty')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 px-3 py-3 text-sm">
              <span className="font-bold text-slate-800"><ContentMetadataText text={row.endpoint.title} /></span>
              <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-slate-600">
                {t(`testCompletion.status.${row.status}`)}
              </span>
              <span className="ml-auto text-xs text-slate-500">{row.activeOperator.name}</span>
              {row.failureCode ? (
                <span className="w-full text-xs font-semibold text-red-700">
                  {t(
                    `common:apiError.${row.failureCode}`,
                    t('testCompletion.history.error', { code: row.failureCode }),
                  )}
                  {typeof row.failureDetails?.message === 'string' ? (
                    <span className="mt-0.5 block break-words font-medium text-red-600">
                      {row.failureDetails.message}
                    </span>
                  ) : null}
                </span>
              ) : null}
              {RESUMABLE_STATUSES.has(row.status) ? (
                <button
                  type="button"
                  onClick={() => void resume(row.id)}
                  disabled={actions.resume.isPending || actions.resumePreflight.isPending}
                  className="rounded-lg border border-cyan-200 bg-white px-2.5 py-1.5 text-xs font-bold text-cyan-800 hover:bg-cyan-50"
                >
                  {t('testCompletion.resume')}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
